import React, { useEffect, useState } from "react";
import { Stack, Tab, TabProps, Tabs } from "@mui/material";
import {
  useFoundationColorTokens,
  useSpacing,
  useTabsColorTokens,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { useDetailSectionHeader } from "../../../../contexts/DetailSectionHeaderContext";

const SURVEY_SETTINGS_PANE_TABS_WIDTH = "256px";

export enum SurveySettingsTab {
  General = "GENERAL",
  SurveyControl = "SURVEY_CONTROL",
  DialogCustomization = "DIALOG_CUSTOMIZATION",
  SurveyUI = "SURVEY_UI",
  AdvancedFeatures = "ADVANCED_FEATURES",
}

export const SurveySettingsPaneTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState(SurveySettingsTab.General);
  const [sectionOffset, setSectionOffset] = useState(0);

  const { t } = useTranslation();
  const spacing = useSpacing();
  const tokens = useFoundationColorTokens();
  const tabsColorTokens = useTabsColorTokens();
  const {
    detailSectionHeaderElement,
    detailsSectionRootContainerElement,
    subscribeToSectionOffset,
  } = useDetailSectionHeader();

  const sectionHeaderHeight = detailSectionHeaderElement?.clientHeight ?? 0;
  const tabContentScrollOffset = sectionHeaderHeight + sectionOffset;

  useEffect(() => subscribeToSectionOffset(setSectionOffset), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const entryId = entry.target.id as SurveySettingsTab;
          if (Object.values(SurveySettingsTab).includes(entryId)) {
            setActiveTab(entryId);
          }
        });
      },
      {
        root: detailsSectionRootContainerElement,
        rootMargin: `-${tabContentScrollOffset}px 0px -100% 0px`,
        threshold: 0,
      },
    );

    Object.values(SurveySettingsTab).forEach((id) => {
      const surveySettingsPaneTab = document.getElementById(id);
      if (surveySettingsPaneTab) observer.observe(surveySettingsPaneTab);
    });

    return () => observer.disconnect();
  }, [sectionHeaderHeight, sectionOffset]);

  const surveySettingsPaneTabs: TabProps[] = [
    {
      label: t("surveys.settings.generalSectionLabel"),
      value: SurveySettingsTab.General,
    },
    {
      label: t("surveys.settings.surveyControlSectionLabel"),
      value: SurveySettingsTab.SurveyControl,
    },
    {
      label: t("surveys.settings.advancedFeaturesSectionLabel"),
      value: SurveySettingsTab.AdvancedFeatures,
    },
    {
      label: t("surveys.settings.dialogCustomizationSectionLabel"),
      value: SurveySettingsTab.DialogCustomization,
    },
    {
      label: t("surveys.settings.surveyUISectionLabel"),
      value: SurveySettingsTab.SurveyUI,
    },
  ];

  return (
    <Stack flex={1}>
      <Tabs
        orientation="vertical"
        textColor="secondary"
        slotProps={{
          indicator: {
            style: { display: "none" },
          },
        }}
        value={activeTab}
        onChange={(_, id) => {
          const sectionElement = document.getElementById(id);
          sectionElement?.style.setProperty(
            "scroll-margin-top",
            `${tabContentScrollOffset}px`,
          );
          sectionElement?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }}
        sx={{
          position: "sticky",
          top: sectionHeaderHeight,
          width: SURVEY_SETTINGS_PANE_TABS_WIDTH,
          flexShrink: 0,
          paddingBlock: spacing.spaceXL,
        }}
      >
        {surveySettingsPaneTabs.map((tabProps) => (
          <Tab
            key={tabProps.value}
            {...tabProps}
            sx={{
              alignItems: "start",
              textTransform: "capitalize",
              ...(activeTab === tabProps.value
                ? {
                    borderLeft: `1px solid transparent`,
                    boxShadow: `inset 2px 0 0 0 ${tokens.borderPrimary}`,
                  }
                : {
                    borderLeft: `1px solid ${tabsColorTokens.tabsBorder}`,
                    fontWeight: 400,
                  }),
            }}
          />
        ))}
      </Tabs>
    </Stack>
  );
};
